import datacentral from '@/utils/axios/datacentral'
import qs from 'query-string'
import { ResponseArray, ResponseObject } from '@/types/shared'
import { DataGridPayload } from 'vnetwork-ui/dist/components/GokuDataGrid'
import { getContactRoleCandidates } from '@/modules/Customer/services'
import { getAllDepartments } from '@/modules/Employee/services'
import {
    Affiliate,
    AffiliateCandidateContact,
    AffiliateCounts,
    AffiliateStats,
    AffiliateDailyStats,
    AffiliateClick,
    AffiliateConversion,
    Payout,
    Commission,
    CommissionSummary,
    CommissionPolicy,
    CommissionCalculationInput,
    CommissionCalculationResult,
    CreateAffiliateRequest,
    UpdateAffiliateRequest,
    CreatePayoutRequest,
    UpdatePayoutRequest,
    PayoutCommission,
    CreateCommissionPolicyRequest,
    UpdateCommissionPolicyRequest,
    CommissionPolicyListParams,
    PendingInvoice,
} from '../types'

// ============= AFFILIATE LIST & PAGINATION =============

export const getAffiliatesList = async (
    payload: DataGridPayload & { referrer_type?: string }
): Promise<ResponseArray<Affiliate>> => {
    const { filters, page, page_size, q, referrer_type } = payload

    const filterParams: Record<string, string> = {}
    if (filters) {
        try {
            const decoded = atob(filters)
            const parsed = JSON.parse(decoded)
            Object.entries(parsed).forEach(([key, value]) => {
                if (value && value !== '') {
                    filterParams[key] = value as string
                }
            })
        } catch (_e) {
            // ignore parse error
        }
    }

    const params: Record<string, any> = {
        search: q || '',
        limit: page_size || 10,
        offset: ((page || 1) - 1) * (page_size || 10),
        ...(referrer_type ? { referrer_type } : {}),
        ...filterParams,
    }

    // Remove empty values
    Object.keys(params).forEach((key) => {
        if (
            params[key] === '' ||
            params[key] === undefined ||
            params[key] === null
        ) {
            delete params[key]
        }
    })

    const response = await datacentral.get(
        `/affiliates?${qs.stringify(params)}`
    )

    // Transform Results to results for GokuDataGrid compatibility (API returns capital R)
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

// The "Chờ xác nhận" tab is a worklist: a contact whose company already has an affiliate is done,
// so it is filtered out server-side (rows AND total together — filtering client-side would leave the
// grid's rowCount reading the unfiltered pagination.total and break paging). Affiliate linkage is
// resolved at COMPANY level, so converting one contact clears every contact of that customer from
// this tab. The linked-state badge/`Xem Affiliate` action in the columns stay as-is: they are still
// reachable for the reseller/supplier tabs, and guard this tab if the flag is ever turned off.
export const getAffiliateCandidates = async (
    payload: DataGridPayload
): Promise<ResponseArray<AffiliateCandidateContact>> => {
    return getContactRoleCandidates({
        ...payload,
        role: 'affiliate',
        exclude_linked: true,
    })
}

// ============= COUNTS =============

// @deprecated No backend route — `GET /affiliates/counts` is not implemented and would 404.
// Do NOT wire this into a component. Kept only to document the intended-but-unbuilt contract (Phase E).
export const getAffiliateCounts = async (params?: {
    search?: string
}): Promise<ResponseObject<AffiliateCounts>> => {
    const response = await datacentral.get(
        `/affiliates/counts?${qs.stringify(params || {})}`
    )
    return response?.data
}

// ============= CRUD =============

export const getAffiliateById = async (
    id: number
): Promise<ResponseObject<Affiliate>> => {
    const response = await datacentral.get(`/affiliates/${id}`)
    return response?.data
}

// ============= PICKER (AsyncFilterSelect) =============

const affiliateOptionLabel = (a: Affiliate) =>
    a.referrer_company
        ? `${a.referrer_name} (${a.referrer_company})`
        : a.referrer_name || a.referrer_email || `#${a.affiliate_id}`

/**
 * Paginated affiliate options for the AsyncFilterSelect picker (infinite scroll).
 * Value = affiliate_id; label = referrer name (+ company/email hint).
 */
export const getAffiliatesOptionsPaginated = async (
    query: string,
    page: number = 1,
    pageSize: number = 5
) => {
    const response = await datacentral.get<ResponseArray<Affiliate>>(
        `/affiliates?${qs.stringify({
            search: query,
            limit: pageSize,
            offset: (page - 1) * pageSize,
            // The picker attributes a customer to a referrer affiliate — employee affiliates are
            // managed separately, so keep them out of this list.
            referrer_type: 'non_employee',
        })}`
    )

    const raw = response?.data as any
    const results: Affiliate[] = raw?.results || raw?.Results || []
    const options = results.map((a) => ({
        label: affiliateOptionLabel(a),
        value: a.affiliate_id,
    }))

    const pagination = raw?.pagination
    // API returns page_next = -1 when no more pages
    const hasMore = pagination ? pagination.page_next !== -1 : false

    return {
        options,
        hasMore,
        nextPage: hasMore && pagination ? pagination.page_next : undefined,
    }
}

/** Resolve affiliate_id(s) to {label, value} options (for preselected values on reload). */
export const getAffiliatesDefaultOptions = async (ids: (string | number)[]) => {
    if (!ids?.length) return []
    const numericIds = ids.map(Number).filter(Boolean)
    const affiliates = await Promise.all(
        numericIds.map(async (id) => {
            try {
                const res = await getAffiliateById(id)
                return res?.result
            } catch {
                return undefined
            }
        })
    )
    return affiliates
        .filter((a): a is Affiliate => Boolean(a))
        .map((a) => ({
            label: affiliateOptionLabel(a),
            value: a.affiliate_id,
        }))
}

export const createAffiliate = async (
    data: CreateAffiliateRequest
): Promise<ResponseObject<Affiliate>> => {
    const response = await datacentral.post('/affiliates', data)
    return response?.data
}

export const updateAffiliate = async ({
    affiliate_id,
    payload,
}: {
    affiliate_id: number
    payload: UpdateAffiliateRequest
}): Promise<ResponseObject<Affiliate>> => {
    const response = await datacentral.put(
        `/affiliates/${affiliate_id}`,
        payload
    )
    return response?.data
}

export const deleteAffiliate = async (
    affiliate_id: number
): Promise<ResponseObject<Affiliate>> => {
    const response = await datacentral.delete(`/affiliates/${affiliate_id}`)
    return response?.data
}

// ============= STATS =============

export const getAffiliateStats = async (
    affiliate_id: number
): Promise<ResponseObject<AffiliateStats>> => {
    const response = await datacentral.get(`/affiliates/${affiliate_id}/stats`)
    return response?.data
}

// @deprecated No backend route — `GET /affiliates/:id/stats/daily` exists only on the partner
// module, not the admin API, so this would 404 here. Do NOT wire into a component (Phase E).
export const getAffiliateDailyStats = async (
    affiliate_id: number,
    params?: { days?: number }
): Promise<ResponseArray<AffiliateDailyStats>> => {
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/stats/daily?${qs.stringify(params || {})}`
    )
    return response?.data
}

// ============= CLICKS =============

export const getAffiliateClicks = async (
    affiliate_id: number,
    payload?: DataGridPayload
): Promise<ResponseArray<AffiliateClick>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/clicks?${qs.stringify(params)}`
    )

    // Transform Results to results for GokuDataGrid compatibility
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

// ============= CONVERSIONS =============

export const getAffiliateConversions = async (
    affiliate_id: number,
    payload?: DataGridPayload
): Promise<ResponseArray<AffiliateConversion>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/conversions?${qs.stringify(params)}`
    )

    // Transform Results to results for GokuDataGrid compatibility
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

// ============= PAYOUTS =============

export const getAffiliatePayouts = async (
    affiliate_id: number,
    payload?: DataGridPayload & { status?: string }
): Promise<ResponseArray<Payout>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    if (payload?.status) {
        params.status = payload.status
    }
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/payouts?${qs.stringify(params)}`
    )

    // Transform Results to results for GokuDataGrid compatibility
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

export const createAffiliatePayout = async ({
    affiliate_id,
    payload,
}: {
    affiliate_id: number
    payload: CreatePayoutRequest
}): Promise<ResponseObject<Payout>> => {
    const response = await datacentral.post(
        `/affiliates/${affiliate_id}/payouts`,
        payload
    )
    return response?.data
}

export const updatePayout = async ({
    payout_id,
    payload,
}: {
    payout_id: number
    payload: UpdatePayoutRequest
}): Promise<ResponseObject<Payout>> => {
    const response = await datacentral.put(`/payouts/${payout_id}`, payload)
    return response?.data
}

// Line items of a settlement batch — which commissions (and which months) a payout covers.
// The backend wraps the array under `result`; unwrap to the array for direct table consumption.
export const getPayoutCommissions = async (
    payout_id: number
): Promise<PayoutCommission[]> => {
    const response = await datacentral.get(`/payouts/${payout_id}/commissions`)
    const data = response?.data as any
    return data?.result ?? data?.results ?? []
}

// ============= COMMISSIONS (Audit Trail) =============

export const getAffiliateCommissions = async (
    affiliate_id: number,
    payload?: DataGridPayload & { status?: string }
): Promise<ResponseArray<Commission>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    if (payload?.status) {
        params.status = payload.status
    }
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/commissions?${qs.stringify(params)}`
    )

    // Transform Results to results for GokuDataGrid compatibility
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

export const getAffiliateCommissionsSummary = async (
    affiliate_id: number
): Promise<ResponseObject<CommissionSummary>> => {
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/commissions/summary`
    )
    return response?.data
}

// ============= PENDING INVOICES =============

export const getAffiliatePendingInvoices = async (
    affiliate_id: number,
    payload?: DataGridPayload & { status?: string }
): Promise<ResponseArray<PendingInvoice>> => {
    const params: Record<string, any> = {
        limit: payload?.page_size || 10,
        offset: ((payload?.page || 1) - 1) * (payload?.page_size || 10),
    }
    if (payload?.status) {
        params.status = payload.status
    }
    if (payload?.q) {
        params.search = payload.q
    }
    const response = await datacentral.get(
        `/affiliates/${affiliate_id}/pending-invoices?${qs.stringify(params)}`
    )
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

// ============= COMMISSION POLICIES =============

export const getCommissionPolicies = async (
    payload: DataGridPayload &
        Omit<CommissionPolicyListParams, 'is_active' | 'scope'> & {
            scope?: string
            is_active?: boolean | string
        }
): Promise<ResponseArray<CommissionPolicy>> => {
    const {
        filters,
        page,
        page_size,
        affiliate_id,
        q,
        scope,
        is_active,
        owner_type,
    } = payload

    const filterParams: Record<string, any> = {}
    if (filters) {
        try {
            const decoded = atob(filters)
            const parsed = JSON.parse(decoded)
            Object.entries(parsed).forEach(([key, value]) => {
                if (value && value !== '') {
                    filterParams[key] = value as string
                }
            })
        } catch (_e) {
            // ignore parse error
        }
    }

    const params: Record<string, any> = {
        q: q || '',
        scope,
        is_active,
        owner_type,
        limit: page_size || 10,
        offset: ((page || 1) - 1) * (page_size || 10),
        ...filterParams,
    }

    if (affiliate_id) {
        params.affiliate_id = affiliate_id
    }

    Object.keys(params).forEach((key) => {
        if (
            params[key] === '' ||
            params[key] === undefined ||
            params[key] === null
        ) {
            delete params[key]
        }
    })

    const response = await datacentral.get(
        `/commission-policies?${qs.stringify(params)}`
    )
    const data = response?.data as any
    if (data?.Results && !data?.results) {
        data.results = data.Results
    }
    return data
}

export const getCommissionPolicyById = async (
    id: number
): Promise<ResponseObject<CommissionPolicy>> => {
    const response = await datacentral.get(`/commission-policies/${id}`)
    return response?.data
}

export const createCommissionPolicy = async (
    data: CreateCommissionPolicyRequest
): Promise<ResponseObject<CommissionPolicy>> => {
    const response = await datacentral.post('/commission-policies', data)
    return response?.data
}

export const updateCommissionPolicy = async ({
    policy_id,
    payload,
}: {
    policy_id: number
    payload: UpdateCommissionPolicyRequest
}): Promise<ResponseObject<CommissionPolicy>> => {
    const response = await datacentral.put(
        `/commission-policies/${policy_id}`,
        payload
    )
    return response?.data
}

export const deleteCommissionPolicy = async (
    policy_id: number
): Promise<ResponseObject<CommissionPolicy>> => {
    const response = await datacentral.delete(
        `/commission-policies/${policy_id}`
    )
    return response?.data
}

// ============= COMMISSION CALCULATOR =============

export const calculateCommission = async (
    data: CommissionCalculationInput
): Promise<ResponseObject<CommissionCalculationResult>> => {
    const response = await datacentral.post(
        '/commission-policies/preview',
        data
    )
    return response?.data
}

// ============= PRODUCTS & PRODUCT GROUPS =============

export const getProductGroups = async (): Promise<
    ResponseArray<{ product_group_id: number; product_group_name: string }>
> => {
    const response = await datacentral.get('/product-groups')
    return response?.data
}

export const getProductGroupOptions = async () => {
    const response = await getProductGroups()
    if (!response?.results) return []
    return response.results.map((item) => ({
        label: item.product_group_name,
        value: item.product_group_id,
    }))
}

// Department options for the policy audience filter (employee-department targeting).
export const getDepartmentOptions = async () => {
    const response = await getAllDepartments()
    if (!response?.results) return []
    return response.results.map((item) => ({
        label: item.department_name,
        value: item.department_id,
    }))
}

export const getProducts = async (): Promise<
    ResponseArray<{ product_id: number; product_name: string }>
> => {
    const response = await datacentral.get('/products')
    return response?.data
}

// Note: Backend endpoints:
// - GET /products - list all products
// - GET /product-groups - list all product groups

export const getProductOptions = async () => {
    const response = await getProducts()
    if (!response?.results) return []
    return response.results.map((item) => ({
        label: item.product_name,
        value: item.product_id,
    }))
}

// Resolve (provision if missing) the affiliate for an employee, so the employee-affiliate list can
// open the standard affiliate detail (/affiliates/:id) with all its tabs.
export const getAffiliateForEmployee = async (
    employeeId: number
): Promise<ResponseObject<Affiliate>> => {
    const response = await datacentral.get(`/employee-affiliate/${employeeId}`)
    return response?.data
}
